import * as types from '../actions/actionTypes';
import {
  OrgTypes,
  generateHierarchyCode,
  getOrgType,
  getAncestorHierarchyCode,
  getOrgName,
} from 'Utils/organizationUtils';
import initialState from './initialState';

// map api response org to application org object
const mapOrg = (apiOrg) => {
  const orgType = getOrgType(apiOrg);
  const hierarchyCode = orgType < 5 ? generateHierarchyCode(apiOrg) : ''; // merchants don't have a standard hierarchyCode
  const name = orgType === OrgTypes.MERCHANT ? apiOrg.merchantName : apiOrg[apiOrg.type.toLowerCase()];
  let parentHierarchyCode = null;
  if (orgType > 0 && orgType < 5) {
    parentHierarchyCode = getAncestorHierarchyCode(orgType - 1, hierarchyCode);
  } else if (orgType === 5) {
    parentHierarchyCode = apiOrg.corp + apiOrg.region + apiOrg.principal + apiOrg.associate + apiOrg.chain;
  }
  const newOrg = {
    hasAccess: true,
    id: orgType < 5 ? hierarchyCode : apiOrg.organizationId,
    orgId: apiOrg.organizationId,
    parentOrganizationId: apiOrg.parentOrganizationId,
    hierarchyCode,
    parentHierarchyCode,
    orgType,
    title: orgType < 5 ? hierarchyCode : apiOrg.organizationId,
    name,
    mainLine: orgType === OrgTypes.MERCHANT ? apiOrg.merchant : name,
    subLine: orgType === OrgTypes.MERCHANT ? apiOrg.merchantName : '',
  };
  if (newOrg.orgType === OrgTypes.MERCHANT) {
    newOrg.merchantNumber = apiOrg.merchant;
  }
  return newOrg;
};

// create a placeholder org from the ancestor metadata of the API org
const createPlaceholderOrg = (orgType, apiOrg) => {
  const apiOrgHierarchyCode = generateHierarchyCode(apiOrg);
  const hierarchyCode = getAncestorHierarchyCode(orgType, apiOrgHierarchyCode);
  const parentHierarchyCode = orgType > 0 ? getAncestorHierarchyCode(orgType - 1, hierarchyCode) : null;
  const name = apiOrg[getOrgName(orgType)];

  return {
    hasAccess: false,
    id: hierarchyCode,
    hierarchyCode,
    parentHierarchyCode,
    orgType,
    name,
    mainLine: name,
    title: hierarchyCode,
    subLine: '',
  };
};

// for non corp top level orgs, create placeholder orgs for ancestors if they aren't already in the cache
const addPlaceholderIfMissing = (orgType, mappedOrg, apiOrg, cachedOrgs) => {
  if (mappedOrg.orgType > orgType) {
    const updatedCachedOrgs = cachedOrgs;
    const placeholder = createPlaceholderOrg(orgType, apiOrg);
    const cacheKey = orgType === OrgTypes.CORP ? 'TOP_LEVEL' : placeholder.parentHierarchyCode;
    updatedCachedOrgs[cacheKey] = updatedCachedOrgs[cacheKey] ? updatedCachedOrgs[cacheKey] : [];
    if (!updatedCachedOrgs[cacheKey].some((c) => c.hierarchyCode === placeholder.hierarchyCode)) {
      updatedCachedOrgs[cacheKey].push(placeholder);
    }
    return updatedCachedOrgs;
  }
  return cachedOrgs;
};

// proess top level organizations for logged in user
const processTopLevelOrgs = (cachedLoginUserOrgs, { apiOrgs }) => {
  let newOrgs = {};
  apiOrgs.forEach((apiOrg) => {
    const mappedOrg = mapOrg(apiOrg);
    const cacheKey = mappedOrg.parentHierarchyCode || 'TOP_LEVEL';
    if (!newOrgs[cacheKey]) {
      newOrgs[cacheKey] = [];
    }
    newOrgs[cacheKey].push(mappedOrg);
    newOrgs = addPlaceholderIfMissing(OrgTypes.CORP, mappedOrg, apiOrg, newOrgs);
    newOrgs = addPlaceholderIfMissing(OrgTypes.REGION, mappedOrg, apiOrg, newOrgs);
    newOrgs = addPlaceholderIfMissing(OrgTypes.PRINCIPAL, mappedOrg, apiOrg, newOrgs);
    newOrgs = addPlaceholderIfMissing(OrgTypes.ASSOCIATE, mappedOrg, apiOrg, newOrgs);
    newOrgs = addPlaceholderIfMissing(OrgTypes.CHAIN, mappedOrg, apiOrg, newOrgs);
  });
  return newOrgs;
};

// process child organizations fetched for a parent org for logged in user
const processOrgs = (cachedLoginUserOrgs, { apiOrgs, parentHierarchyCode }) => {
  const newOrgs = apiOrgs.map((apiOrg) => {
    return mapOrg(apiOrg);
  });
  const updatedOrgsCache = { ...cachedLoginUserOrgs };
  updatedOrgsCache[parentHierarchyCode] = newOrgs;
  return updatedOrgsCache;
};

export default function organizationReducer(state = initialState.organization, action) {
  switch (action.type) {
    case types.REQUEST_LOGINUSER_ORGANIZATIONS:
      return Object.assign({}, state, {
        isFetching: true,
        error: null,
      });

    case types.REQUEST_ORGANIZATION_MERCHANT_COUNT:
      return Object.assign({}, state, {
        countIsFetching: true,
        error: null,
      });
    case types.RECEIVE_LOGINUSER_ORGANIZATIONS_SUCCESS: {
      const cachedLoginUserOrgs = state.cachedLoginUserOrgs;
      const isTopLevel = !action.parentHierarchyCode;
      const processorFunction = isTopLevel ? processTopLevelOrgs : processOrgs;
      const updatedOrgsCache = processorFunction(cachedLoginUserOrgs, action);

      return Object.assign({}, state, {
        isFetching: false,
        cachedLoginUserOrgs: updatedOrgsCache,
        lastUpdated: action.receivedAt,
        error: null,
      });
    }

    case types.RECEIVE_LOGINUSER_ORGANIZATIONS_FAILURE:
      return Object.assign({}, state, {
        isFetching: false,
        error: action.error,
      });

    case types.RECEIVE_ORGANIZATION_MERCHANT_COUNT_SUCCESS: {
      const updatedCachedMerchantCounts = state.cachedMerchantCounts || {};
      updatedCachedMerchantCounts[action.parentHierarchyCode] = action.merchantCount;
      return Object.assign({}, state, {
        cachedMerchantCounts: updatedCachedMerchantCounts,
        countIsFetching: false,
        error: null,
        lastUpdaed: action.receivedAt,
      });
    }

    default:
      return state;
  }
}
